import moment, { lang, CALENDAR_FLOOR } from './calendarMoment.js'
import { element, renderWeekRow, renderWeekdayHeader } from './calendarHelpers.js'

// `order` is a list of CONCEPTS, each concept being every slug that names it —
// across languages (WPML terms are fetched per language, so the English terms
// have their own slugs: Veranstaltungen→shows, Zugaben→on-top, Kurse→courses)
// and across environments (older/corrected local-DB slugs kept as fallbacks).
// Grouping by concept rather than listing slugs flat is what makes the filter
// order identical in every language: the concept's position is its rank, so
// "Zugaben" and "On Top" both sort second regardless of how their slugs would
// sort alphabetically. A term whose slug is in no group falls to the end.
const TAXONOMIES = [
  //{ slug: 'event-format',   key: 'formats',   label: { de: 'Format',     en: 'Format'   } },
  { slug: 'event-type',     key: 'types',     label: { de: 'Typ',        en: 'Type'     }, order: [
    ['veranstaltungen', 'veranstaltung', 'shows'],
    ['zugaben', 'zugabe', 'on-top'],
    ['trainings'],
    ['kurse', 'courses'],
    ['workshops'],
    ['kinderkurse'],
  ] },
  { slug: 'event-skill',    key: 'skills',    label: { de: 'Skill',      en: 'Skill'    }, order: [
    ['fuer-alle-levels', 'offe-fuer-alle', 'all-levels'],
    ['profis', 'professional'],
    ['open-practice'],
  ] },
  { slug: 'event-audience', key: 'audiences', label: { de: 'Zielgruppe', en: 'Audience' }, order: [
    ['erwachsene', 'adults'],
    ['jugendliche', 'youth'],
    ['kinder', 'kids'],
  ] },
]

// Order a taxonomy's filter terms by the concept list above. Listed concepts sort
// to the front in list order; everything else keeps the REST default (alphabetical
// by name) — Array.sort is stable, so unlisted terms hold their relative order.
const sortTerms = (terms, order) => {
  if (!order) return terms
  const rank = (slug) => {
    const i = order.findIndex(group => group.includes(slug))
    return i === -1 ? order.length : i
  }
  return [...terms].sort((a, b) => rank(a.slug) - rank(b.slug))
}

const fetchJSON = async (url) => {
  const r = await fetch(url)
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  return r.json()
}

// Read the calendar's deep-link state from the query string: per-taxonomy filter
// slugs (keyed by TAXONOMIES[].key) and the target month (YYYY-MM). Slugs are
// validated against the live, visible terms by the caller — here we only parse
// and shape. An invalid/garbage month parses to null.
const parseUrlState = () => {
  const params = new URLSearchParams(location.search)
  const requestedFilters = {}
  for (const t of TAXONOMIES) {
    const raw = params.get(t.key)
    requestedFilters[t.key] = raw ? raw.split(',').map(s => s.trim()).filter(Boolean) : []
  }
  const monthRaw = params.get('month')
  const monthMoment = monthRaw && /^\d{4}-\d{2}$/.test(monthRaw)
    ? moment(monthRaw, 'YYYY-MM', true)
    : null
  return {
    requestedFilters,
    monthMoment: monthMoment && monthMoment.isValid() ? monthMoment.startOf('month') : null,
  }
}

const INITIAL_WEEKS = 8       // current week + buffer rendered on first paint
const SCROLL_BATCH = 6        // weeks appended each time you near the bottom
const FUTURE_CAP_MONTHS = 18  // stop infinite forward scroll this far ahead
const floorMonth = moment(CALENDAR_FLOOR, 'YYYY-MM-DD').startOf('month') // earliest navigable month (launch)

// The full week-stack is desktop-only (≥768px). On phones the calendar page shows
// the shared mini-calendar inline instead (template-parts/detail/calendar.php), so
// we skip init entirely below that width — no taxonomy/event fetches on mobile.
const desktop = window.matchMedia('(min-width: 768px)')

const initCalendar = async (calendar) => {
  if (calendar.dataset.calendarInited) return
  calendar.dataset.calendarInited = '1'

  const restUrl = window.php_vars.restUrl
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  const monthKey = (m) => m.format('YYYY-MM')

  const state = {
    fromWeek: moment().startOf('isoWeek'), // current ISO week (Mon) — the top boundary
    topWeek: null,                         // Monday of the topmost rendered week
    bottomWeek: null,                      // Monday of the bottom-most rendered week
    loadedMonths: new Set(),
    eventIds: new Set(),
    events: [],
    filters: Object.fromEntries(TAXONOMIES.map(t => [t.key, new Set()])),
  }
  const futureLimit = state.fromWeek.clone().add(FUTURE_CAP_MONTHS, 'months').endOf('month')

  // The first ISO week whose Thursday falls in `target`'s month — the week
  // scrollToMonth would land on. Used both to bound navigation and to decide when
  // the ← arrow is dead (so the disabled state matches the actual reachability).
  const targetWeekFor = (target) => {
    const tw = target.clone().startOf('month').startOf('isoWeek')
    if (tw.clone().add(3, 'days').month() !== target.month()) tw.add(7, 'days')
    return tw
  }

  // Past floor: the launch month's week. A ?month= before it, or ← from the floor,
  // won't render anything earlier — the site holds no events before July 2026.
  const pastLimit = targetWeekFor(floorMonth)

  // Taxonomy terms for the filter UI. Editors can flag individual tags
  // (`hide_in_filter` term meta, exposed via REST) to keep them out of the UI.
  const taxonomies = await Promise.all(
    TAXONOMIES.map(async (t) => {
      // Pass the page language so WPML returns the current language's terms
      // (names + slugs). Without it the REST default is the site default (de),
      // so the English calendar would show German filter labels and slugs that
      // never match the (English) event-row taxonomy classes.
      const terms = await fetchJSON(`${restUrl}wp/v2/${t.slug}?per_page=100&lang=${lang}`).catch(() => [])
      const visible = terms.filter(term => !term.meta?.hide_in_filter)
      return { ...t, terms: sortTerms(visible, t.order) }
    })
  )

  // Restore deep-link state from the URL before the checkbox loop and first paint,
  // so the grid renders already-filtered and already-positioned. Keep only filter
  // slugs that map to a visible term (drops unknown / hide_in_filter-hidden slugs).
  const urlState = parseUrlState()
  for (const t of taxonomies) {
    const valid = new Set(t.terms.map(term => term.slug))
    for (const slug of urlState.requestedFilters[t.key] || []) {
      if (valid.has(slug)) state.filters[t.key].add(slug)
    }
  }
  const initialMonth = urlState.monthMoment

  // Events are fetched a month at a time and accumulated. No filter params — the
  // grid now holds every event and filtering hides/show rows client-side (#5).
  const loadMonth = async (m) => {
    const key = monthKey(m)
    if (state.loadedMonths.has(key)) return
    state.loadedMonths.add(key)
    const params = new URLSearchParams({ year: m.year(), month: m.month() + 1, lang })
    const data = await fetchJSON(`${restUrl}glunz/v1/event-dates/month?${params}`).catch(() => ({ dates: [] }))
    const rows = Array.isArray(data) ? data : (data?.dates ?? [])
    // Month payloads are padded to whole ISO weeks, so a week straddling two
    // months (e.g. Mon–Sun crossing Sep/Oct) is present in BOTH payloads. Dedupe
    // on the event-date row id so such a row isn't accumulated — and rendered —
    // twice on its day.
    for (const row of rows) {
      if (row.id != null) {
        if (state.eventIds.has(row.id)) continue
        state.eventIds.add(row.id)
      }
      state.events.push(row)
    }
  }
  // A week can straddle two months — fetch both before rendering it.
  const ensureWeekLoaded = async (weekStart) => {
    await loadMonth(weekStart.clone().startOf('month'))
    const sunday = weekStart.clone().add(6, 'days')
    if (sunday.month() !== weekStart.month()) await loadMonth(sunday.startOf('month'))
  }

  // — header —
  const monthLabel = element('span', 'calendar-header-label fs-small')
  const header = element('header', 'calendar-header fs-small')
  const prev = element('button', 'button-accessible calendar-header-nav calendar-header-prev', { type: 'button', 'aria-label': lang === 'de' ? 'Vergangene Wochen' : 'Past weeks' })
  prev.textContent = '←'
  const next = element('button', 'button-accessible calendar-header-nav calendar-header-next', { type: 'button', 'aria-label': lang === 'de' ? 'Nächster Monat' : 'Next month' })
  next.textContent = '→'
  header.append(prev, monthLabel, next)

  // — filters: one fieldset per taxonomy (Typ / Skill / Zielgruppe) —
  const filters = element('div', 'calendar-filters fs-small')
  for (const t of taxonomies) {
    if (!t.terms.length) continue // no visible tags (none, or all hidden) → no group
    const group = element('fieldset', 'calendar-filter', { 'data-taxonomy': t.slug })
    const legend = element('legend', 'calendar-filter-legend hidden')
    legend.textContent = t.label[lang]
    group.appendChild(legend)
    for (const term of t.terms) {
      const optionLabel = element('label', 'calendar-filter-option')
      const cb = element('input', 'calendar-filter-checkbox', { type: 'checkbox', value: term.slug })
      if (state.filters[t.key].has(term.slug)) cb.checked = true // restored from the URL
      cb.addEventListener('change', () => {
        const set = state.filters[t.key]
        cb.checked ? set.add(term.slug) : set.delete(term.slug)
        applyFilters()
        syncUrl()
      })
      const text = element('span', 'calendar-filter-label')
      text.textContent = term.name
      optionLabel.append(cb, text)
      group.appendChild(optionLabel)
    }
    filters.appendChild(group)
  }

  // — body: a sticky controls block (month nav + filters + weekday row) over a
  // vertical stack of week rows that flows in the page. `.reveal-enabled` arms the
  // hidden resting state for the row reveal (so content is never stuck invisible
  // if the bundle fails). —
  const weekdays = renderWeekdayHeader(state.fromWeek)
  const controls = element('div', 'calendar-controls')
  controls.append(header, filters, weekdays)
  const stack = element('div', 'calendar-view-stack')
  const body = element('div', 'calendar-body reveal-enabled')
  body.append(controls, stack)

  // Toggle `is-stuck` on the controls while they're pinned to the top, so the stuck
  // state can be styled apart from the in-flow state. A zero-height sentinel just
  // above the controls flips the class exactly as it crosses the sticky line (the
  // site header height) — no per-frame scroll polling, and it fires once on load.
  const headerH = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0
  const stuckSentinel = element('div', 'calendar-stuck-sentinel', { 'aria-hidden': 'true' })
  body.insertBefore(stuckSentinel, controls)
  new IntersectionObserver(
    ([entry]) => controls.classList.toggle('is-stuck', !entry.isIntersecting),
    { rootMargin: `-${headerH}px 0px 0px 0px`, threshold: 0 },
  ).observe(stuckSentinel)

  // Publish the controls block's live height so each sticky .calendar-day-number
  // pins right below the weekday row (its CSS `top` = header + this height). The
  // filter row wraps with the viewport width, so track it rather than hard-coding.
  const syncControlsHeight = () =>
    calendar.style.setProperty('--calendar-controls-height', `${controls.getBoundingClientRect().height}px`)
  new ResizeObserver(syncControlsHeight).observe(controls)
  syncControlsHeight()

  // Reveal each week's events row by row as the week scrolls into view (#1). The
  // page viewport is the observer root (like AOS) — the stack flows in the page,
  // there's no inner scroll container.
  const observer = reduceMotion ? null : new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue
      entry.target.classList.add('is-revealed')
      observer.unobserve(entry.target)
    }
  }, { rootMargin: '0px 0px -10% 0px', threshold: 0.05 })

  const buildWeekRow = (weekStart) => {
    const row = renderWeekRow(weekStart.clone(), undefined, state.events)
    row.dataset.weekStart = weekStart.format('YYYY-MM-DD')
    // Per-item index (events + day dividers) drives the staggered reveal delay,
    // so a divider fades up in sequence with the events around it (70_calendar.sass).
    row.querySelectorAll('.calendar-event, .calendar-day-divider').forEach((el, i) => el.style.setProperty('--i', i))
    if (observer) observer.observe(row)
    else row.classList.add('is-revealed') // reduced motion: show immediately
    return row
  }

  // Client-side filtering, in place (#5): non-matches become visibility:hidden so
  // they vanish but keep their slot — the grid never reflows. A taxonomy with
  // selections matches OR within it; taxonomies AND together (same as the old
  // server-side query). Match targets are the per-taxonomy classes on the <li>
  // (see calendarHelpers eventTaxonomyClasses).
  const applyFilters = (root = stack) => {
    const active = TAXONOMIES
      .map(t => ({ slug: t.slug, terms: state.filters[t.key] }))
      .filter(a => a.terms.size)
    root.querySelectorAll('.calendar-event').forEach((el) => {
      const show = active.every(a => [...a.terms].some(term => el.classList.contains(`${a.slug}-${term}`)))
      el.classList.toggle('is-filtered-out', !show)
    })

    // A day's divider only earns its rule while events remain visible on BOTH
    // sides of it (regular above, Veranstaltung/Show below). Filter either group
    // away and the rule would hang there separating nothing, so hide it too —
    // same `is-filtered-out` treatment, so it keeps its slot and nothing reflows.
    const visibleEventAfter = (node, step) => {
      for (let n = node[step]; n; n = n[step]) {
        if (n.classList.contains('calendar-event') && !n.classList.contains('is-filtered-out')) return true
      }
      return false
    }
    root.querySelectorAll('.calendar-day-divider').forEach((divider) => {
      const between = visibleEventAfter(divider, 'previousElementSibling')
        && visibleEventAfter(divider, 'nextElementSibling')
      divider.classList.toggle('is-filtered-out', !between)
    })
  }

  const weekRowFor = (m) => stack.querySelector(`[data-week-start="${m.format('YYYY-MM-DD')}"]`)

  // Space at the top of the viewport taken by the fixed site header + the sticky
  // weekday row, so a programmatic scroll lands a week just below the weekday row.
  const stickyOffset = () => {
    const siteHeader = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0
    return siteHeader + controls.getBoundingClientRect().height
  }

  // The site sets html { scroll-behavior: smooth } globally; force an instant jump
  // when we need one (prepend compensation, reduced motion) by overriding inline.
  const scrollInstant = (top) => {
    const el = document.documentElement
    const prev = el.style.scrollBehavior
    el.style.scrollBehavior = 'auto'
    window.scrollTo(0, top)
    el.style.scrollBehavior = prev
  }

  // The week sitting just below the sticky controls block — drives the month label.
  const topVisibleWeek = () => {
    const cut = controls.getBoundingClientRect().bottom + 1
    for (const row of stack.children) {
      if (row.getBoundingClientRect().bottom > cut) return moment(row.dataset.weekStart, 'YYYY-MM-DD')
    }
    return null
  }
  // Deep-link state → URL. replaceState only: the URL stays a shareable snapshot
  // of the current filters + visible month without flooding the back stack. Seeds
  // from the live query string and re-appends the hash, so foreign params
  // (e.g. ?kategorie= from loadMore) and tab/toggle hash deep-links survive.
  let lastSyncedMonth = initialMonth ? monthKey(initialMonth) : null
  const syncUrl = () => {
    const params = new URLSearchParams(location.search)
    for (const t of TAXONOMIES) {
      const set = state.filters[t.key]
      if (set && set.size) params.set(t.key, [...set].join(','))
      else params.delete(t.key)
    }
    const w = topVisibleWeek() || state.topWeek
    const m = w ? monthKey(w.clone().add(3, 'days')) : null // Thursday-rule month = label
    if (m) params.set('month', m)
    else params.delete('month')
    const qs = params.toString()
    history.replaceState(null, '', `${location.pathname}${qs ? '?' + qs : ''}${location.hash}`)
  }

  const updateMonthLabel = () => {
    const w = topVisibleWeek() || state.topWeek
    if (!w) return
    // Thursday represents the ISO week's month (doy:4), so a week straddling two
    // months is labelled by the one it mostly belongs to.
    const thursday = w.clone().add(3, 'days')
    monthLabel.textContent = thursday.format('MMMM YYYY')
    // Grey out ← once the previous month would fall before the past cap — it no-ops
    // there anyway (scrollToMonth returns early), so disabling matches the behaviour.
    prev.disabled = targetWeekFor(thursday.clone().startOf('month').subtract(1, 'month')).isBefore(pastLimit, 'day')
    // Continuous, deduped month sync: the rAF scroll handler calls this every
    // frame, so only touch the URL when the month actually changes.
    const m = monthKey(thursday)
    if (m !== lastSyncedMonth) {
      lastSyncedMonth = m
      syncUrl()
    }
  }

  // Append future weeks — the initial fill and the lazy load as you near the bottom.
  let appending = false
  const appendWeeks = async (count) => {
    if (appending) return
    appending = true
    try {
      for (let n = 0; n < count; n++) {
        const week = state.bottomWeek ? state.bottomWeek.clone().add(7, 'days') : state.fromWeek.clone()
        if (week.isAfter(futureLimit, 'day')) break // stop infinite forward scroll
        await ensureWeekLoaded(week)
        stack.appendChild(buildWeekRow(week))
        state.bottomWeek = week
        if (!state.topWeek) state.topWeek = week.clone()
      }
      applyFilters()
      updateMonthLabel()
    } finally {
      appending = false
    }
  }

  // ← prepend the previous month of weeks (fully grayed via .is-past), keeping the
  // viewport stable so nothing jumps — the past is then just a scroll up away (#3).
  let prepending = false
  const prependPastMonth = async () => {
    if (prepending || !state.topWeek) return
    prepending = true
    try {
      const target = state.topWeek.clone().subtract(1, 'month').startOf('isoWeek')
      const rows = []
      for (const c = target.clone(); c.isBefore(state.topWeek, 'day'); c.add(7, 'days')) {
        await ensureWeekLoaded(c)
        rows.push(buildWeekRow(c.clone()))
      }
      if (!rows.length) return
      // Keep the viewport visually stable. With overflow-anchor:none the browser
      // won't auto-adjust, so we shift the page scroll by exactly the height we
      // prepend — instant, so a CSS smooth-scroll can't animate (or fight) it.
      const y0 = window.scrollY
      const h0 = stack.offsetHeight
      const frag = document.createDocumentFragment()
      rows.forEach(r => frag.appendChild(r))
      stack.insertBefore(frag, stack.firstChild)
      state.topWeek = target
      applyFilters()
      scrollInstant(y0 + (stack.offsetHeight - h0))
    } finally {
      prepending = false
    }
  }

  // → jump forward to the next month (rendering ahead first if needed).
  const gotoNextMonth = async () => {
    const top = topVisibleWeek() || state.topWeek
    const target = top.clone().add(1, 'month').startOf('isoWeek')
    if (target.isAfter(futureLimit, 'day')) return
    if (!state.bottomWeek || state.bottomWeek.isBefore(target, 'day')) {
      await appendWeeks(target.diff(state.bottomWeek || state.fromWeek, 'weeks') + 2)
    }
    const row = weekRowFor(target)
    if (!row) return
    const y = Math.max(0, row.getBoundingClientRect().top + window.scrollY - stickyOffset())
    if (reduceMotion) scrollInstant(y)
    else window.scrollTo({ top: y, behavior: 'smooth' })
  }

  // Bring an arbitrary month into view for a deep link. Works in both directions:
  // append forward (future) or prepend month-by-month (past) until the target week
  // exists, then scroll it under the sticky controls. The target week is the first
  // ISO week whose Thursday lands in the month, matching updateMonthLabel's rule so
  // the restored position and the label agree. Bounded by futureLimit / pastLimit
  // so an old or absurd ?month= can't loop the stack open.
  const scrollToMonth = async (target, { instant = false } = {}) => {
    const targetWeek = targetWeekFor(target)
    if (targetWeek.isAfter(futureLimit, 'day') || targetWeek.isBefore(pastLimit, 'day')) return

    if (!targetWeek.isBefore(state.fromWeek, 'day')) {
      // Future (or current-but-ahead): append forward until the week is rendered.
      while (!weekRowFor(targetWeek)) {
        const before = state.bottomWeek ? state.bottomWeek.clone() : null
        await appendWeeks(targetWeek.diff(state.bottomWeek || state.fromWeek, 'weeks') + 2)
        if (!state.bottomWeek) break
        if (before && state.bottomWeek.isSame(before, 'day')) break // hit the cap, stop
      }
    } else {
      // Past: prepend a month at a time until the week is rendered.
      while (!weekRowFor(targetWeek)) {
        if (!state.topWeek) break
        if (state.topWeek.clone().subtract(1, 'month').startOf('isoWeek').isBefore(pastLimit, 'day')) break
        const before = state.topWeek.clone()
        await prependPastMonth()
        if (state.topWeek.isSame(before, 'day')) break // didn't move → stop
      }
    }

    const row = weekRowFor(targetWeek)
    if (!row) return
    const y = Math.max(0, row.getBoundingClientRect().top + window.scrollY - stickyOffset())
    if (instant || reduceMotion) scrollInstant(y)
    else window.scrollTo({ top: y, behavior: 'smooth' })
    updateMonthLabel()
  }

  // ← jump back to the previous month. scrollToMonth prepends past weeks as needed
  // and scrolls them under the sticky controls, so the arrow visibly moves the view
  // — not just silently load past weeks above the fold (prependPastMonth alone keeps
  // the viewport stable, which reads as a dead button). Step back from the month the
  // header shows (Thursday rule: top week + 3 days), not the month the raw top week's
  // Monday falls in — otherwise ← from e.g. JULI skips to MAI instead of JUNI.
  const gotoPrevMonth = async () => {
    const top = topVisibleWeek() || state.topWeek
    if (!top) return
    const shownMonth = top.clone().add(3, 'days').startOf('month')
    await scrollToMonth(shownMonth.subtract(1, 'month'))
  }

  prev.addEventListener('click', gotoPrevMonth)
  next.addEventListener('click', gotoNextMonth)

  // Lazy-load future weeks as the bottom of the stack nears the viewport; keep the
  // month label in sync. The page scrolls naturally — no inner scroll container.
  let scrollScheduled = false
  window.addEventListener('scroll', () => {
    if (scrollScheduled) return
    scrollScheduled = true
    requestAnimationFrame(() => {
      scrollScheduled = false
      updateMonthLabel()
      if (stack.getBoundingClientRect().bottom <= window.innerHeight * 1.5) {
        appendWeeks(SCROLL_BATCH)
      }
    })
  }, { passive: true })

  // Group-hover (profitraining): hovering a Ballett / Zeitgenössisch training flags
  // every other event sharing that discipline tag with `.is-tag-highlight`, so the
  // whole group lights up and you can scan the month for it. Delegated on `stack`
  // so lazily-loaded weeks are covered without re-binding; pointerover bubbles,
  // pointerleave clears when the pointer exits the stack.
  const GROUP_HOVER_TAGS = ['tag-profitraining-ballett', 'tag-profitraining-zeitgenoessisch']
  let hoverTag = null
  const clearGroupHover = () => {
    if (!hoverTag) return
    stack.querySelectorAll('.is-tag-highlight').forEach(el => el.classList.remove('is-tag-highlight'))
    hoverTag = null
  }
  stack.addEventListener('pointerover', (e) => {
    const li = e.target.closest('.calendar-event')
    const tag = li ? (GROUP_HOVER_TAGS.find(t => li.classList.contains(t)) || null) : null
    if (tag === hoverTag) return        // same group (or both null) → nothing to do
    clearGroupHover()
    if (!tag) return
    hoverTag = tag
    stack.querySelectorAll(`.${tag}`).forEach(el => el.classList.add('is-tag-highlight'))
  })
  stack.addEventListener('pointerleave', clearGroupHover)

  // — first paint: the stack starts at the current week, flowing into the page —
  calendar.replaceChildren(body)
  calendar.classList.add('is-loading')
  try {
    await appendWeeks(INITIAL_WEEKS)
    // Deep link to a non-current month → jump there (past prepends, future appends).
    // The current month already shows from today on first paint, so skip the scroll
    // for it rather than rewinding to the 1st.
    if (initialMonth && !initialMonth.isSame(moment(), 'month')) {
      await scrollToMonth(initialMonth, { instant: true })
    }
  } finally {
    calendar.classList.remove('is-loading')
  }
}

const initCalendars = () => {
  if (!desktop.matches) return
  document.querySelectorAll('[data-calendar]').forEach(initCalendar)
}

initCalendars()
// Grow into desktop later (rotate / resized window) → init the stack then.
desktop.addEventListener('change', initCalendars)
